import PropTypes from 'prop-types';
import Modal from 'modules/common/components/Modal/Modal';
import { useAlternativeNames } from '../../hooks';
import servicesStrings from '../../strings';
import AlternativeNameItem from './AlternativeNameItem';
import {
  AlternativeNamesSection,
  UnsavedChangesIndicator,
  ErrorMessage,
  AddAlternativeForm,
  AlternativeInput,
  AddButton,
  AlternativeNamesList,
  NoAlternativesText,
  LoadingText,
} from './AlternativeNamesModal.style';

const AlternativeNamesModal = ({ isOpen, onClose, service }) => {
  const {
    alternativeNames,
    newAlternativeName,
    isLoading,
    isSaving,
    error,
    hasUnsavedChanges,
    setNewAlternativeName,
    addAlternative,
    deleteAlternative,
    saveChanges,
    handleUpdateAlternativeName,
  } = useAlternativeNames(service, isOpen, onClose);

  return (
    <Modal
      data-testid="AlternativeModal"
      title={`${service?.serviceNameEn} ${servicesStrings.alternativeNames.modalTitle}`}
      onClose={onClose}
      onCancelHandler={onClose}
      toggle={isOpen}
      onConfirmHandler={saveChanges}
      confirmActionLabel={
        isSaving
          ? servicesStrings.alternativeNames.savingButton
          : servicesStrings.alternativeNames.saveButton
      }
      disableConfirmButton={!hasUnsavedChanges || isSaving}
      cancelActionLabel={servicesStrings.alternativeNames.cancelButton}
    >
      <AlternativeNamesSection>
        {hasUnsavedChanges && (
          <UnsavedChangesIndicator>
            {servicesStrings.alternativeNames.unsavedChangesIndicator}
          </UnsavedChangesIndicator>
        )}

        {error && <ErrorMessage>{error}</ErrorMessage>}

        <AddAlternativeForm>
          <AlternativeInput
            type="text"
            placeholder={servicesStrings.alternativeNames.addAlternativePlaceholder}
            value={newAlternativeName}
            onChange={e => setNewAlternativeName(e.target.value)}
          />
          <AddButton onClick={addAlternative} disabled={!newAlternativeName.trim()}>
            {servicesStrings.alternativeNames.addAlternativeButton}
          </AddButton>
        </AddAlternativeForm>

        <AlternativeNamesList>
          {isLoading && <LoadingText>{servicesStrings.alternativeNames.loadingText}</LoadingText>}
          {!isLoading && alternativeNames.length === 0 && (
            <NoAlternativesText>
              {servicesStrings.alternativeNames.noAlternativesText}
            </NoAlternativesText>
          )}
          {!isLoading &&
            alternativeNames.length > 0 &&
            alternativeNames.map((alternative, index) => (
              <AlternativeNameItem
                key={alternative.alternativeName || index}
                alternativeName={alternative.alternativeName}
                onUpdate={handleUpdateAlternativeName}
                onDelete={deleteAlternative}
              />
            ))}
        </AlternativeNamesList>
      </AlternativeNamesSection>
    </Modal>
  );
};

AlternativeNamesModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  service: PropTypes.shape({
    serviceKey: PropTypes.string,
    serviceNameEn: PropTypes.string,
    serviceNameAr: PropTypes.string,
    cpt: PropTypes.string,
  }),
};

AlternativeNamesModal.defaultProps = {
  service: null,
};

export default AlternativeNamesModal;
